import axios from "axios";
import {
  authenticatedBitswanBackendInstance,
  getBackendHost,
} from "@/lib/api-client";
import type {
  AdvisorTrialRow,
  BillingChange,
  BillingDetails,
  Invoice,
  OnboardingState,
  TrialStatus,
} from "@/types/billing";

export type SignupInput = {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
};

export type SignupResult = {
  access_token: string;
  verification_email_sent: boolean;
  user: { id: string; email: string };
};

export async function signup(input: SignupInput) {
  const base = `https://${getBackendHost(window.location.hostname)}/api/frontend`;
  const res = await axios.post(`${base}/public/signup/`, input);
  return res.data as SignupResult;
}

export const PRICING_TIERS = [
  {
    slug: "personal",
    name: "Personal / non-profit",
    blurb:
      "For one person, a community project or a registered non-profit. The lowest prices we charge.",
    highlights: [
      "The lowest prices we charge",
      "An automation advisor you choose",
    ],
  },
  {
    slug: "small_business",
    name: "Small business",
    blurb: "For a company running its own automations. Standard prices, nothing to negotiate.",
    highlights: [
      "Standard prices, nothing to negotiate",
      "An automation advisor you choose",
    ],
  },
  {
    slug: "enterprise",
    name: "Enterprise",
    blurb:
      "For larger organisations that need their own terms and features. BitSwan works out a plan with you.",
    highlights: [
      "DORA support",
      "NIS2 support",
      "Single sign-on with your own provider",
      "Dedicated support",
    ],
  },
];

const TIER_KEY = "aoc.pricing_tier";

export function rememberPricingTier(slug: string) {
  try {
    window.localStorage.setItem(TIER_KEY, slug);
  } catch {
    // A browser that refuses storage falls back to the default below.
  }
}

export function recallPricingTier() {
  try {
    const stored = window.localStorage.getItem(TIER_KEY);
    if (stored && PRICING_TIERS.some((tier) => tier.slug === stored)) return stored;
  } catch {
    // ignored, see above
  }
  return "small_business";
}

export function forgetPricingTier() {
  try {
    window.localStorage.removeItem(TIER_KEY);
  } catch {
    // ignored, see above
  }
}

export async function createOnboardingOrganization(name: string, pricingTier: string) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post("/onboarding/organization/", { name, pricing_tier: pricingTier });
  return res.data as {
    id: string;
    name: string;
    plan: string;
    pricing_tier: string;
    trial_ends_at: string;
    days_left: number;
  };
}

export async function fetchOnboardingState(orgId?: string) {
  const api = await authenticatedBitswanBackendInstance(
    orgId ? { "X-Org-Id": orgId } : {},
  );
  const res = await api.get("/onboarding/state/");
  return res.data as OnboardingState;
}

export async function chooseAdvisor(advisorProfileId: string, orgId?: string) {
  const api = await authenticatedBitswanBackendInstance(
    orgId ? { "X-Org-Id": orgId } : {},
  );
  const res = await api.post("/onboarding/advisor/", {
    advisor_profile_id: advisorProfileId,
  });
  return res.data as {
    advisor_profile_id: string;
    advisor_display_name: string;
  };
}

export async function requestPublicOffer(offerId: string, packageId: string) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post(`/public-offers/${offerId}/request/`, { package: packageId });
  return res.data;
}

export async function fetchBillingDetails(orgId?: string) {
  const api = await authenticatedBitswanBackendInstance(
    orgId ? { "X-Org-Id": orgId } : {},
  );
  const res = await api.get("/billing/details/");
  return res.data as BillingDetails;
}

export async function saveBillingDetails(
  details: Omit<BillingDetails, "exists">,
  orgId?: string,
) {
  const api = await authenticatedBitswanBackendInstance(
    orgId ? { "X-Org-Id": orgId } : {},
  );
  const res = await api.put("/billing/details/", details);
  return res.data as BillingDetails;
}

export async function fetchBillingHistory(orgId?: string) {
  const api = await authenticatedBitswanBackendInstance(
    orgId ? { "X-Org-Id": orgId } : {},
  );
  const res = await api.get("/billing/details/history/");
  return res.data as BillingChange[];
}

export async function fetchTrialStatus(orgId?: string) {
  const api = await authenticatedBitswanBackendInstance(orgId ? { "X-Org-Id": orgId } : {});
  const res = await api.get("/billing/trial/");
  return res.data as TrialStatus;
}

export async function fetchInvoices() {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get("/billing/invoices/");
  return res.data as Invoice[];
}

export async function downloadInvoicePdf(invoice: Invoice) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get(`/billing/invoices/${invoice.id}/pdf/`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(res.data as Blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `invoice_${invoice.tracking_number ?? invoice.invoice_number}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function fetchAdvisorTrials() {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get("/advisor/trials/");
  return res.data as AdvisorTrialRow[];
}


export async function fetchEmailVerification() {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get("/signup/verify-email/");
  return res.data as { email: string; verified: boolean };
}

export async function confirmEmail(code: string) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post("/signup/verify-email/", { code });
  return res.data as { verified: boolean };
}

export async function resendEmailCode() {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post("/signup/verify-email/resend/");
  return res.data as { sent: boolean };
}

export type ServerPricing = {
  currency: string | null;
  license_monthly_price: string | null;
  free_until: string | null;
  tier: string;
  tier_name: string;
  plan: string | null;
  plan_name: string | null;
  features: Array<{ slug: string; name: string; description: string }>;
};

export async function fetchServerPricing(orgId?: string) {
  const api = await authenticatedBitswanBackendInstance(orgId ? { "X-Org-Id": orgId } : {});
  const res = await api.get("/billing/server-pricing/");
  return res.data as ServerPricing;
}
